import { useEffect, useMemo, useState } from "react";
import { Chip, IconButton, Menu, MenuItem } from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  MoreVert,
  Star as StarIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useConfirm } from "@accredo/fe-shared/contexts/ConfirmContext.jsx";
import { useInsurerContactActions } from "../../../hooks/useInsurerContactActions.jsx";
import { useInsurerContactPermissions } from "../../../hooks/permissions/useInsurerContactPermissions.jsx";
import InsurerContactFormModal from "./InsurerContactFormModal.jsx";

export default function InsurerContactsTable({ insurerId, onDefaultChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const { contacts, isLoading, hasLoaded, load, create, update, makeDefault, remove } =
    useInsurerContactActions(insurerId);
  const { canCreate, canEdit, canDelete, canMakeDefault } = useInsurerContactPermissions();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurerId]);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(row) {
    if (!canEdit()) return;
    setEditing(row);
    setModalOpen(true);
  }

  function openMenu(e, row) {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  }

  function closeMenu() {
    setMenuAnchor(null);
    setMenuRow(null);
  }

  async function handleMakeDefault(row) {
    closeMenu();
    try {
      await makeDefault(row.id);
      onDefaultChanged?.();
      enqueueSnackbar("Standard-Kontakt festgelegt", { variant: "success" });
    } catch {
      enqueueSnackbar("Standard-Kontakt konnte nicht festgelegt werden", {
        variant: "error",
      });
    }
  }

  async function handleDelete(row) {
    closeMenu();
    const confirmed = await confirm({
      title: "Kontakt löschen",
      message: `Kontakt „${row.display_name}" wirklich löschen? Bereits gesendete Mitteilungen bleiben unverändert nachvollziehbar.`,
      confirmLabel: "Löschen",
      destructive: true,
    });
    if (!confirmed) return;
    try {
      await remove(row.id);
      enqueueSnackbar("Kontakt gelöscht", { variant: "success" });
    } catch {
      enqueueSnackbar("Kontakt konnte nicht gelöscht werden", {
        variant: "error",
      });
    }
  }

  const columns = useMemo(
    () =>
      [
        {
          accessorKey: "display_name",
          header: "Name",
          Cell: ({ row }) => (
            <>
              {row.original.display_name}
              {row.original.is_default && (
                <Chip size="small" label="Standard" sx={{ ml: 1 }} />
              )}
            </>
          ),
        },
        { accessorKey: "email", header: "E-Mail-Adresse" },
        (canMakeDefault() || canDelete()) && {
          id: "actions",
          header: "",
          size: 56,
          enableSorting: false,
          enableColumnFilter: false,
          enableColumnHiding: false,
          Cell: ({ row }) =>
            row.original.is_default ? null : (
              <IconButton
                aria-label="Aktionen"
                size="small"
                onClick={(e) => openMenu(e, row.original)}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            ),
        },
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={contacts}
        isLoading={isLoading}
        hasLoaded={hasLoaded}
        manualSorting={false}
        manualFiltering={false}
        enableGlobalFilter={true}
        enableColumnFilters={false}
        onRowClick={openEdit}
        toolbarActions={[
          canCreate() && {
            type: "button",
            label: "Neu",
            icon: <AddIcon />,
            onClick: openCreate,
            variant: "primary",
          },
        ].filter(Boolean)}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {canMakeDefault() && (
          <MenuItem onClick={() => handleMakeDefault(menuRow)}>
            <StarIcon fontSize="small" sx={{ mr: 1 }} />
            Als Standard festlegen
          </MenuItem>
        )}
        {canDelete() && (
          <MenuItem onClick={() => handleDelete(menuRow)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Löschen
          </MenuItem>
        )}
      </Menu>

      <InsurerContactFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        contact={editing}
        onCreate={create}
        onUpdate={update}
      />
    </>
  );
}

InsurerContactsTable.propTypes = {
  onDefaultChanged: PropTypes.func,
  insurerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};
